import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Divider, Chip, Avatar,
  Stack, IconButton, Tooltip, TextField, Button, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import {
  Person as PersonIcon, School as SchoolIcon, DateRange as DateIcon,
  LocationOn as LocationIcon, ContactPage as ContactIcon, Badge as BadgeIcon,
  Refresh as RefreshIcon, Lock as LockIcon, Security as SecurityIcon,
  CheckCircle as CheckIcon, QrCode as QrCodeIcon, Close as CloseIcon,
  Visibility, VisibilityOff, Email as EmailIcon, ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { studentAPI } from '../../services/api';
import Loading from '../common/Loading';

// ─── Change Password Section ─────────────────────────────────────────────────
const ChangePasswordSection = () => {
  const [form, setForm] = useState({ current: '', newPass: '', confirm: '' });
  const [showPasswords, setShowPasswords] = useState(false);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.newPass !== form.confirm) {
      setMsg({ type: 'error', text: 'Les mots de passe ne correspondent pas' });
      return;
    }
    if (form.newPass.length < 6) {
      setMsg({ type: 'error', text: 'Le mot de passe doit contenir au moins 6 caractères' });
      return;
    }
    setLoading(true);
    setMsg(null);
    try {
      await studentAPI.changePassword(form.current, form.newPass);
      setMsg({ type: 'success', text: 'Mot de passe modifié avec succès' });
      setForm({ current: '', newPass: '', confirm: '' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Erreur lors du changement de mot de passe' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card sx={{ borderRadius: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <LockIcon sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h6" fontWeight="600">Changer le mot de passe</Typography>
        </Box>
        <Divider sx={{ mb: 3 }} />
        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              label="Mot de passe actuel"
              type={showPasswords ? 'text' : 'password'}
              value={form.current}
              onChange={(e) => setForm({ ...form, current: e.target.value })}
              fullWidth size="small"
              InputProps={{
                endAdornment: (
                  <IconButton size="small" onClick={() => setShowPasswords(!showPasswords)}>
                    {showPasswords ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                )
              }}
            />
            <TextField
              label="Nouveau mot de passe"
              type={showPasswords ? 'text' : 'password'}
              value={form.newPass}
              onChange={(e) => setForm({ ...form, newPass: e.target.value })}
              fullWidth size="small"
            />
            <TextField
              label="Confirmer le nouveau mot de passe"
              type={showPasswords ? 'text' : 'password'}
              value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              fullWidth size="small"
            />
            {msg && <Alert severity={msg.type}>{msg.text}</Alert>}
            <Button
              type="submit" variant="contained" disabled={loading || !form.current || !form.newPass || !form.confirm}
              startIcon={loading ? <CircularProgress size={18} /> : <LockIcon />}
            >
              {loading ? 'Modification...' : 'Modifier le mot de passe'}
            </Button>
          </Stack>
        </form>
      </CardContent>
    </Card>
  );
};

// ─── 2FA Section ─────────────────────────────────────────────────────────────
const TwoFactorSection = () => {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState(null); // { qrCode, secret }
  const [code, setCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [msg, setMsg] = useState(null);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    studentAPI.get2FAStatus().then(r => {
      setEnabled(r.data.enabled);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleSetup = async () => {
    setActionLoading(true);
    setMsg(null);
    try {
      const r = await studentAPI.setup2FA();
      setSetupData(r.data);
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Erreur lors de la configuration' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerify = async () => {
    if (code.length !== 6) return;
    setActionLoading(true);
    setMsg(null);
    try {
      await studentAPI.verify2FA(code);
      setEnabled(true);
      setSetupData(null);
      setCode('');
      setMsg({ type: 'success', text: '2FA activé avec succès ! Votre compte est maintenant sécurisé.' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Code invalide' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisable = async () => {
    if (disableCode.length !== 6) return;
    setActionLoading(true);
    setMsg(null);
    try {
      await studentAPI.disable2FA(disableCode);
      setEnabled(false);
      setShowDisableDialog(false);
      setDisableCode('');
      setMsg({ type: 'info', text: '2FA désactivé' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Code invalide' });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <Card sx={{ borderRadius: 3 }}><CardContent><CircularProgress size={24} /></CardContent></Card>;

  return (
    <Card sx={{ borderRadius: 3, border: enabled ? '2px solid #2ecc71' : '2px solid #eee' }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SecurityIcon sx={{ mr: 2, color: enabled ? '#2ecc71' : 'text.secondary' }} />
            <Typography variant="h6" fontWeight="600">Authentification à deux facteurs (2FA)</Typography>
          </Box>
          <Chip
            label={enabled ? 'Activé' : 'Désactivé'}
            color={enabled ? 'success' : 'default'}
            icon={enabled ? <CheckIcon /> : undefined}
          />
        </Box>
        <Divider sx={{ mb: 3 }} />

        {msg && <Alert severity={msg.type} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}

        {enabled ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Votre compte est protégé par l'authentification à deux facteurs.
            </Alert>
            <Button variant="outlined" color="error" onClick={() => setShowDisableDialog(true)}>
              Désactiver le 2FA
            </Button>
          </Box>
        ) : setupData ? (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <strong>Étape 1:</strong> Scannez ce QR code avec votre application d'authentification (Google Authenticator, Authy, etc.)
            </Typography>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Box component="img" src={setupData.qrCode} alt="QR Code 2FA" sx={{ width: 200, height: 200, border: '1px solid #eee', borderRadius: 2 }} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Ou entrez ce code manuellement dans l'application:
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: '#f5f5f5', p: 1, borderRadius: 1, mb: 3, wordBreak: 'break-all' }}>
              {setupData.secret}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <strong>Étape 2:</strong> Entrez le code à 6 chiffres affiché dans l'application pour confirmer
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                label="Code de vérification"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                size="small"
                inputProps={{ maxLength: 6, style: { letterSpacing: 6, fontSize: 20, textAlign: 'center' } }}
                sx={{ width: 180 }}
              />
              <Button
                variant="contained" color="success"
                onClick={handleVerify}
                disabled={code.length !== 6 || actionLoading}
                startIcon={actionLoading ? <CircularProgress size={18} /> : <CheckIcon />}
              >
                Activer
              </Button>
              <Button variant="text" onClick={() => { setSetupData(null); setCode(''); }}>
                Annuler
              </Button>
            </Stack>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Ajoutez une couche de sécurité supplémentaire à votre compte. Vous aurez besoin d'une application comme Google Authenticator ou Authy.
            </Typography>
            <Button
              variant="contained"
              startIcon={actionLoading ? <CircularProgress size={18} /> : <QrCodeIcon />}
              onClick={handleSetup}
              disabled={actionLoading}
            >
              Configurer le 2FA
            </Button>
          </Box>
        )}
      </CardContent>

      {/* Disable dialog */}
      <Dialog open={showDisableDialog} onClose={() => setShowDisableDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Désactiver le 2FA
          <IconButton size="small" onClick={() => setShowDisableDialog(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Entrez le code de votre application d'authentification pour confirmer.
          </Typography>
          <TextField
            label="Code à 6 chiffres"
            value={disableCode}
            onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            fullWidth size="small"
            inputProps={{ maxLength: 6, style: { letterSpacing: 6, fontSize: 20, textAlign: 'center' } }}
          />
          {msg && msg.type === 'error' && <Alert severity="error" sx={{ mt: 2 }}>{msg.text}</Alert>}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setShowDisableDialog(false)} color="inherit">Annuler</Button>
          <Button
            variant="contained" color="error"
            onClick={handleDisable}
            disabled={disableCode.length !== 6 || actionLoading}
          >
            {actionLoading ? 'En cours...' : 'Désactiver'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

// ─── Main Profile Page ────────────────────────────────────────────────────────
// ─── Institutional Email Card ─────────────────────────────────────────────────
const InstitutionalEmailCard = () => {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [showPass, setShowPass] = useState(false);
  const [copied, setCopied]     = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    studentAPI.getInstitutionalEmail()
      .then(d => setData(d?.email ? d : null))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const copy = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(''), 2000);
    });
  };

  if (loading || !data) return null;

  return (
    <Card sx={{ borderRadius: 3, border: '1.5px solid #bae6fd', bgcolor: '#f0f9ff' }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <EmailIcon sx={{ mr: 1.5, color: '#0284c7', fontSize: 22 }} />
          <Typography variant="h6" fontWeight={700} sx={{ color: '#0284c7' }}>
            Email Institutionnel
          </Typography>
        </Box>
        <Divider sx={{ mb: 2.5, borderColor: '#bae6fd' }} />

        {/* Email */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>Adresse email</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Typography variant="body1" fontWeight={700} sx={{ color: '#0c4a6e', flex: 1, wordBreak: 'break-all' }}>
              {data.email}
            </Typography>
            <Tooltip title={copied === 'email' ? 'Copié !' : 'Copier'}>
              <IconButton size="small" onClick={() => copy(data.email, 'email')} sx={{ color: '#0284c7' }}>
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Password */}
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>Mot de passe par défaut</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Typography variant="body1" fontWeight={700} sx={{ color: '#0c4a6e', flex: 1, fontFamily: showPass ? 'monospace' : 'inherit', letterSpacing: showPass ? 1 : 4 }}>
              {showPass ? data.password : '••••••••'}
            </Typography>
            <Tooltip title={showPass ? 'Masquer' : 'Afficher'}>
              <IconButton size="small" onClick={() => setShowPass(v => !v)} sx={{ color: '#0284c7' }}>
                {showPass ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
              </IconButton>
            </Tooltip>
            {showPass && (
              <Tooltip title={copied === 'pass' ? 'Copié !' : 'Copier'}>
                <IconButton size="small" onClick={() => copy(data.password, 'pass')} sx={{ color: '#0284c7' }}>
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Alert severity="info" sx={{ mt: 2.5, borderRadius: 2, fontSize: '0.78rem' }}>
          Utilisez ces identifiants pour accéder à votre messagerie institutionnelle.
        </Alert>
      </CardContent>
    </Card>
  );
};

const StudentProfile = () => {
  const [studentData, setStudentData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { t, i18n } = useTranslation();

  const fetchStudentData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await studentAPI.getProfile();
      setStudentData(response.student);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load student data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchStudentData(); }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString(i18n.language === 'ar' ? 'ar-MA' : 'fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch { return dateString; }
  };

  const getGenderLabel = (code) => {
    if (code === 'M') return t('male');
    if (code === 'F') return t('female');
    return code;
  };

  if (isLoading) return <Loading message={t('loading')} />;
  if (error) return <Box sx={{ p: 3, textAlign: 'center' }}><Typography color="error">{error}</Typography></Box>;
  if (!studentData) return null;

  const InfoRow = ({ icon, label, value, isArabic = false }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <Box sx={{ mr: 2, color: 'primary.main' }}>{icon}</Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>{label}</Typography>
        <Typography variant="body1" fontWeight={500} sx={{ direction: isArabic ? 'rtl' : 'ltr', fontFamily: isArabic ? 'Arabic UI Text, Arial' : 'inherit' }}>
          {value || '-'}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="600" color="primary" gutterBottom>{t('profileTitle')}</Typography>
        <Typography variant="body1" color="text.secondary">{t('profileDescription')}</Typography>
      </Box>

      {/* Header with Avatar */}
      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar
              src={studentAPI.getStudentPhotoUrl(studentData.cod_etu)}
              sx={{ width: 80, height: 80, mr: 3, background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)', fontSize: '2rem', fontWeight: 'bold' }}
            >
              {studentData.nom_complet?.charAt(0) || 'S'}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" gutterBottom fontWeight="600" color="primary">{studentData.nom_complet}</Typography>
              {studentData.nom_arabe && (
                <Typography variant="h6" color="text.secondary" sx={{ direction: 'rtl', fontFamily: 'Arabic UI Text, Arial', mb: 1 }}>
                  {studentData.nom_arabe}
                </Typography>
              )}
              <Chip icon={<BadgeIcon />} label={`${t('studentCode')}: ${studentData.cod_etu}`} color="primary" variant="outlined" sx={{ mt: 1 }} />
            </Box>
            <Tooltip title={t('refreshData')}>
              <IconButton onClick={fetchStudentData} color="primary"><RefreshIcon /></IconButton>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Personal Info */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <PersonIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="600">{t('personalInfo')}</Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <Stack spacing={2}>
                <InfoRow icon={<ContactIcon />} label={t('cin')} value={studentData.cin} />
                <InfoRow icon={<DateIcon />} label={t('dateOfBirth')} value={formatDate(studentData.date_naissance)} />
                <InfoRow icon={<LocationIcon />} label={t('placeOfBirth')} value={studentData.lieu_naissance} />
                {studentData.lieu_naissance_arabe && (
                  <InfoRow icon={<LocationIcon />} label={t('placeOfBirthArabic')} value={studentData.lieu_naissance_arabe} isArabic />
                )}
                <InfoRow icon={<PersonIcon />} label={t('gender')} value={getGenderLabel(studentData.sexe)} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Academic Info */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <SchoolIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="600">{t('academicInfo')}</Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <Stack spacing={2}>
                <InfoRow icon={<SchoolIcon />} label={t('specialization')} value={studentData.etape} />
                <InfoRow icon={<BadgeIcon />} label={t('specializationLicense')} value={studentData.licence_etape} />
                <InfoRow icon={<SchoolIcon />} label={t('diploma')} value={studentData.diplome} />
                <InfoRow icon={<DateIcon />} label={t('academicYear')} value={studentData.annee_universitaire} />
                <InfoRow icon={<DateIcon />} label={t('lastUpdate')} value={formatDate(studentData.derniere_mise_a_jour)} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Institutional Email */}
        <Grid item xs={12} md={6}>
          <InstitutionalEmailCard />
        </Grid>

        {/* Security — Change Password */}
        <Grid item xs={12} md={6}>
          <ChangePasswordSection />
        </Grid>

        {/* Security — 2FA */}
        <Grid item xs={12} md={6}>
          <TwoFactorSection />
        </Grid>
      </Grid>
    </Box>
  );
};

export default StudentProfile;
